import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2,
  MessageCircle, FileSpreadsheet, Smartphone, AppWindow,
  Brain, Target, Globe2, Building2, Link2, Rocket, TrendingUp,
  Users, Stethoscope, GraduationCap, UtensilsCrossed,
  Scissors, Dumbbell, ShoppingBag, Wrench,
  Calendar, CreditCard, Shield, BarChart3, Star,
  Check, X, Zap, Heart, Phone, Mail, DollarSign,
  ArrowRight, Clock, MapPin, Banknote, Receipt,
  Bell, UserCheck, BookOpen, ChefHat, Store
} from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_rapid-dev-calc/artifacts/lgmmvoy4_Novatide%20Logo%20New.png';

/* ========== UTILITY COMPONENTS ========== */

const GradientText = ({ children, className = '' }) => (
  <span className={`gradient-text ${className}`}>{children}</span>
);

const GlassCard = ({ children, className = '', glow, purple }) => (
  <div className={`glass-card ${glow ? 'glass-card-glow' : ''} ${purple ? 'glass-card-purple' : ''} ${className}`}>
    {children}
  </div>
);

const IconBox = ({ icon: Icon, size = 24, className = '' }) => (
  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${className}`}>
    <Icon size={size} />
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-teal-400 font-semibold text-xs tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Outfit' }}>
    {children}
  </p>
);

const MVPBadge = () => (
  <span className="badge-mvp">
    <Star size={10} /> MVP
  </span>
);

/* ========== SLIDE 1: TITLE ========== */
const TitleSlide = () => (
  <div className="slide-content flex flex-col items-center justify-center min-h-[70vh] text-center" data-testid="slide-title">
    <div>
      <img src={LOGO} alt="Novatide" className="w-20 h-20 sm:w-24 sm:h-24 mx-auto drop-shadow-2xl" style={{ animation: 'float 4s ease-in-out infinite' }} />
    </div>
    <div>
      <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight mt-6">
        <GradientText>Novatide</GradientText>
      </h1>
    </div>
    <div>
      <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mt-5 font-light max-w-2xl leading-relaxed px-2">
        AI-Powered Operating System for Local Businesses
      </p>
    </div>
    <div>
      <p className="text-sm sm:text-base text-slate-500 mt-2 italic px-2">
        Where every workflow is an intelligent agent
      </p>
    </div>
    <div>
      <div className="mt-10 sm:mt-14 flex items-center gap-3 text-slate-600 text-xs sm:text-sm">
        <span>Press</span>
        <kbd className="px-2 py-1 rounded border border-slate-700 bg-slate-800/50 text-slate-400 text-xs font-mono">
          &rarr;
        </kbd>
        <span>or click to navigate</span>
      </div>
    </div>
  </div>
);

/* ========== SLIDE 2: PROBLEM ========== */
const ProblemSlide = () => {
  const pains = [
    { icon: MessageCircle, title: 'WhatsApp for bookings', desc: 'No system, no records, missed appointments', color: 'bg-teal-500/10 text-teal-400' },
    { icon: FileSpreadsheet, title: 'Excel for inventory', desc: 'Manual errors, outdated data, zero insights', color: 'bg-purple-500/10 text-purple-400' },
    { icon: Banknote, title: 'Paper invoices', desc: 'Tax nightmares, lost receipts, compliance gaps', color: 'bg-amber-500/10 text-amber-400' },
    { icon: AppWindow, title: '5+ separate apps', desc: 'Nothing talks to each other, data silos everywhere', color: 'bg-rose-500/10 text-rose-400' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-problem">
      <div><SectionLabel>The Problem</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
          400M+ SMBs globally.<br />
          <span className="text-slate-500">Most run on chaos.</span>
        </h2>
      </div>
      <div>
        <p className="text-base sm:text-lg text-slate-400 mt-3 max-w-2xl">
          In India, 63M MSMEs. In Australia, 2.5M SMEs. Nearly all struggle with the same broken workflows.
        </p>
      </div>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-6 sm:mt-8">
          {pains.map((p) => (
            <GlassCard key={p.title} className="flex items-start gap-4 !py-5">
              <IconBox icon={p.icon} className={p.color} />
              <div>
                <h3 className="text-white font-semibold text-sm">{p.title}</h3>
                <p className="text-slate-500 text-xs mt-1 leading-relaxed">{p.desc}</p>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>
      <div>
        <p className="text-slate-600 text-xs mt-6 flex items-center gap-2">
          <Heart size={12} className="text-rose-500" />
          82% of small businesses fail due to cash flow problems &mdash; U.S. Bank Study
        </p>
      </div>
    </div>
  );
};

/* ========== SLIDE 3: SOLUTION ========== */
const SolutionSlide = () => {
  const pillars = [
    { icon: Brain, title: 'AI Agents, Not Rules', desc: 'Agents that think, decide, and act \u2014 not rigid if-then automations', color: 'bg-teal-500/10 text-teal-400' },
    { icon: Target, title: 'Industry-Specific', desc: 'Pre-built for doctors, tutors, restaurants \u2014 not blank canvases', color: 'bg-purple-500/10 text-purple-400' },
    { icon: Globe2, title: 'Country-Aware', desc: 'India runs on WhatsApp + UPI. Australia on SMS + Stripe. Novatide knows.', color: 'bg-cyan-500/10 text-cyan-400' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-solution">
      <div><SectionLabel>The Solution</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
          Meet <GradientText>Novatide</GradientText>
        </h2>
      </div>
      <div>
        <p className="text-lg sm:text-xl text-slate-300 mt-3 font-light">
          Your AI Crew. Your Business Autopilot.
        </p>
      </div>
      <div>
        <p className="text-sm sm:text-base text-slate-500 mt-2 max-w-3xl leading-relaxed">
          Not just another automation tool. Novatide deploys a team of AI agents &mdash; each specialized, each learning, all collaborating &mdash; tailored to your industry and your country.
        </p>
      </div>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mt-6 sm:mt-8">
          {pillars.map((p, i) => (
            <GlassCard key={p.title} glow={i === 0} purple={i === 1} className="flex flex-col items-start">
              <IconBox icon={p.icon} className={p.color} />
              <h3 className="text-white font-bold text-lg mt-4">{p.title}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{p.desc}</p>
            </GlassCard>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ========== SLIDE 4: HOW IT WORKS ========== */
const HowItWorksSlide = () => {
  const steps = [
    { num: '01', icon: Building2, title: 'Tell us your business', desc: 'Select your industry and we auto-generate your AI crew' },
    { num: '02', icon: Link2, title: 'Connect your tools', desc: 'WhatsApp, UPI, Calendar, SMS, Stripe \u2014 plug in what you use' },
    { num: '03', icon: Rocket, title: 'Deploy in minutes', desc: 'Your AI team starts working immediately \u2014 no setup hassle' },
    { num: '04', icon: TrendingUp, title: 'Watch them learn', desc: 'Agents get smarter with every interaction and optimize themselves' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-how-it-works">
      <div><SectionLabel>How It Works</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
          Four steps to <GradientText>autopilot</GradientText>
        </h2>
      </div>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-8 sm:mt-10">
          {steps.map((s, i) => (
            <div key={s.num} className="relative">
              <GlassCard className="flex flex-col items-start h-full">
                <span className="text-3xl font-extrabold text-slate-800 mb-3" style={{ fontFamily: 'Syne' }}>{s.num}</span>
                <IconBox icon={s.icon} className="bg-teal-500/10 text-teal-400 mb-3" />
                <h3 className="text-white font-semibold text-base">{s.title}</h3>
                <p className="text-slate-500 text-xs mt-2 leading-relaxed">{s.desc}</p>
              </GlassCard>
              {i < 3 && (
                <div className="absolute top-1/2 -right-4 z-20 text-slate-700 hidden lg:block">
                  <ArrowRight size={16} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ========== SLIDE 5: AI CREW ========== */
const AICrewSlide = () => {
  const agents = [
    { icon: Phone, name: 'AI Receptionist', desc: 'Manages bookings, responds to customer inquiries 24/7, handles cancellations and rescheduling', color: 'bg-teal-500/10 text-teal-400' },
    { icon: TrendingUp, name: 'AI Marketing Manager', desc: 'Sends targeted promotions, collects reviews, runs re-engagement campaigns automatically', color: 'bg-purple-500/10 text-purple-400' },
    { icon: Receipt, name: 'AI Bookkeeper', desc: 'Generates invoices, tracks payments, sends reminders, provides tax-ready summaries', color: 'bg-amber-500/10 text-amber-400' },
    { icon: Shield, name: 'AI Operations Manager', desc: 'Monitors inventory, schedules staff, tracks KPIs, and surfaces actionable insights', color: 'bg-cyan-500/10 text-cyan-400' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-ai-crew">
      <div><SectionLabel>The Differentiator</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
          Your <GradientText>AI Crew</GradientText>
        </h2>
      </div>
      <div>
        <p className="text-sm sm:text-base text-slate-400 mt-2">They collaborate. They learn. They never sleep.</p>
      </div>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 mt-6 sm:mt-8">
          {agents.map((a, i) => (
            <GlassCard key={a.name} glow={i === 0} className="flex items-start gap-4">
              <IconBox icon={a.icon} className={a.color} />
              <div>
                <h3 className="text-white font-bold">{a.name}</h3>
                <p className="text-slate-400 text-sm mt-1.5 leading-relaxed">{a.desc}</p>
              </div>
            </GlassCard>
          ))}
        </div>
      </div>
      <div>
        <p className="text-slate-600 text-xs mt-6 italic">
          Unlike single-agent tools, Novatide agents work as a team &mdash; sharing context, delegating tasks, and escalating intelligently.
        </p>
      </div>
    </div>
  );
};

/* ========== SLIDE 6: INDUSTRIES ========== */
const IndustriesSlide = () => {
  const industries = [
    { icon: Stethoscope, name: 'Doctor / Clinic', mvp: true, desc: 'Appointments, follow-ups, billing' },
    { icon: GraduationCap, name: 'Tutoring / Coaching', mvp: true, desc: 'Attendance, fees, parent updates' },
    { icon: UtensilsCrossed, name: 'Restaurant', mvp: true, desc: 'Orders, bookings, reviews' },
    { icon: Scissors, name: 'Salon & Spa', desc: 'Bookings, loyalty, reviews' },
    { icon: Building2, name: 'Real Estate', desc: 'Leads, viewings, listings' },
    { icon: Dumbbell, name: 'Gym & Fitness', desc: 'Memberships, classes, engagement' },
    { icon: ShoppingBag, name: 'Local Retail', desc: 'Inventory, credit, delivery' },
    { icon: Wrench, name: 'Trades & Services', desc: 'Jobs, quotes, compliance' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-industries">
      <div><SectionLabel>Industries We Power</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white">
          Start with 3. <span className="text-slate-500">Scale to all.</span>
        </h2>
      </div>
      <div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-6 sm:mt-8">
          {industries.map((ind) => (
            <GlassCard key={ind.name} glow={ind.mvp} className="flex flex-col items-start relative !p-5">
              {ind.mvp && <div className="absolute top-3 right-3"><MVPBadge /></div>}
              <IconBox icon={ind.icon} className={ind.mvp ? 'bg-teal-500/10 text-teal-400' : 'bg-slate-500/10 text-slate-400'} />
              <h3 className="text-white font-semibold text-sm mt-3">{ind.name}</h3>
              <p className="text-slate-500 text-xs mt-1">{ind.desc}</p>
            </GlassCard>
          ))}
        </div>
      </div>
      <div>
        <p className="text-slate-500 text-sm mt-6">
          Each industry gets pre-configured AI agents, workflow templates, and country-specific integrations out of the box.
        </p>
      </div>
    </div>
  );
};

/* ========== SLIDE 7: DOCTOR MVP ========== */
const DoctorSlide = () => {
  const agents = [
    {
      icon: Calendar, name: 'AI Appointment Agent',
      tasks: ['WhatsApp/SMS booking with availability check', 'OPD token generation & queue position', 'Automated reminders 24h & 2h before', 'No-show follow-up & rescheduling'],
      color: 'bg-teal-500/10 text-teal-400'
    },
    {
      icon: Heart, name: 'AI Follow-up Agent',
      tasks: ['Post-visit care instructions via WhatsApp', 'Medicine reminders on schedule', '"How are you feeling?" automated check-ins', 'Specialist referral suggestions if needed'],
      color: 'bg-purple-500/10 text-purple-400'
    },
    {
      icon: CreditCard, name: 'AI Billing Agent',
      tasks: ['Auto-generate GST/Medicare compliant invoices', 'Insurance & health fund claim processing', 'UPI/Stripe payment collection links', 'Overdue payment reminders'],
      color: 'bg-cyan-500/10 text-cyan-400'
    },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-doctor">
      <div className="flex items-center gap-3">
        <MVPBadge />
        <Stethoscope size={24} className="text-teal-400" />
      </div>
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2">Doctor / Clinic</h2>
        <p className="text-slate-400 text-sm mt-1">Three AI agents working together to run your practice</p>
      </div>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mt-6 sm:mt-7">
          {agents.map((a) => (
            <GlassCard key={a.name} glow className="flex flex-col">
              <IconBox icon={a.icon} className={a.color} />
              <h3 className="text-white font-bold mt-3">{a.name}</h3>
              <ul className="mt-3 space-y-2 flex-1">
                {a.tasks.map((t) => (
                  <li key={t} className="flex items-start gap-2 text-xs text-slate-400">
                    <Check size={12} className="text-teal-400 mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ========== SLIDE 8: TUTOR MVP ========== */
const TutorSlide = () => {
  const agents = [
    {
      icon: UserCheck, name: 'AI Attendance Agent',
      tasks: ['Automated attendance marking', 'Instant absent alerts to parents via WhatsApp', 'Pattern detection (frequent absences)', 'Monthly attendance reports'],
      color: 'bg-teal-500/10 text-teal-400'
    },
    {
      icon: DollarSign, name: 'AI Fee Collector',
      tasks: ['Due date reminders via WhatsApp/SMS', 'UPI/Stripe payment link generation', 'Instant receipt confirmation', 'Pending fee dashboard for owner'],
      color: 'bg-purple-500/10 text-purple-400'
    },
    {
      icon: Mail, name: 'AI Parent Communicator',
      tasks: ['Weekly progress updates to parents', 'Test score sharing with context', 'PTM scheduling & reminders', 'Holiday & event announcements'],
      color: 'bg-amber-500/10 text-amber-400'
    },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-tutor">
      <div className="flex items-center gap-3">
        <MVPBadge />
        <GraduationCap size={24} className="text-purple-400" />
      </div>
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2">Tutoring / Coaching</h2>
        <p className="text-slate-400 text-sm mt-1">Keep students engaged and parents informed automatically</p>
      </div>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mt-6 sm:mt-7">
          {agents.map((a) => (
            <GlassCard key={a.name} glow className="flex flex-col">
              <IconBox icon={a.icon} className={a.color} />
              <h3 className="text-white font-bold mt-3">{a.name}</h3>
              <ul className="mt-3 space-y-2 flex-1">
                {a.tasks.map((t) => (
                  <li key={t} className="flex items-start gap-2 text-xs text-slate-400">
                    <Check size={12} className="text-teal-400 mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ========== SLIDE 9: RESTAURANT MVP ========== */
const RestaurantSlide = () => {
  const agents = [
    {
      icon: Smartphone, name: 'AI Order Manager',
      tasks: ['WhatsApp/call order intake', 'Kitchen notification & status updates', 'Zomato & Swiggy order sync (India)', 'Uber Eats & DoorDash sync (Australia)'],
      color: 'bg-teal-500/10 text-teal-400'
    },
    {
      icon: Calendar, name: 'AI Booking & Delivery Agent',
      tasks: ['Table reservation & waitlist management', 'Porter / Uber Package delivery coordination', 'SMS/WhatsApp confirmation & tracking', 'No-show tracking & delivery ETA updates'],
      color: 'bg-amber-500/10 text-amber-400'
    },
    {
      icon: Star, name: 'AI Review Collector',
      tasks: ['Post-meal feedback via WhatsApp', 'Google Review link with prompt', 'Negative review instant escalation', 'Loyalty points & reward tracking'],
      color: 'bg-purple-500/10 text-purple-400'
    },
  ];
  const integrations = [
    { label: 'Zomato', region: 'IN' },
    { label: 'Swiggy', region: 'IN' },
    { label: 'Porter', region: 'IN' },
    { label: 'Uber Eats', region: 'AU' },
    { label: 'DoorDash', region: 'AU' },
    { label: 'Uber Package', region: 'AU' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-restaurant">
      <div className="flex items-center gap-3">
        <MVPBadge />
        <UtensilsCrossed size={24} className="text-amber-400" />
      </div>
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2">Restaurant</h2>
        <p className="text-slate-400 text-sm mt-1">From order to delivery to review &mdash; integrated with the platforms your customers already use</p>
      </div>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mt-6">
          {agents.map((a) => (
            <GlassCard key={a.name} glow className="flex flex-col">
              <IconBox icon={a.icon} className={a.color} />
              <h3 className="text-white font-bold mt-3">{a.name}</h3>
              <ul className="mt-3 space-y-2 flex-1">
                {a.tasks.map((t) => (
                  <li key={t} className="flex items-start gap-2 text-xs text-slate-400">
                    <Check size={12} className="text-teal-400 mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </GlassCard>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mt-5 flex-wrap">
          <span className="text-slate-500 text-xs font-medium mr-1">Platform Integrations:</span>
          {integrations.map((int) => (
            <span key={int.label} className="px-3 py-1 rounded-full text-[10px] font-semibold border bg-white/[0.02] border-white/[0.08] text-slate-300">
              {int.label} <span className="text-slate-600 ml-0.5">{int.region}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ========== SLIDE 10: INDIA MARKET ========== */
const IndiaMarketSlide = () => {
  const features = [
    { icon: MessageCircle, label: 'WhatsApp-First', desc: '500M+ users in India' },
    { icon: Smartphone, label: 'UPI Payments', desc: 'Instant payment links via Razorpay' },
    { icon: Shield, label: 'GST Compliance', desc: 'Auto-generate GST invoices & filing summaries' },
    { icon: Globe2, label: 'Hindi + Regional', desc: 'Workflows in Hindi, Tamil, Telugu & more' },
    { icon: Calendar, label: 'Festival Calendar', desc: 'Auto-trigger Diwali, Holi, Eid promotions' },
    { icon: BookOpen, label: 'Khata / Udhar', desc: 'Built-in credit tracking for Indian retail' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-india">
      <div><SectionLabel>Market: India</SectionLabel></div>
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-3xl sm:text-4xl font-bold text-white">India-First Features</h2>
        <span className="text-3xl">&#127470;&#127475;</span>
      </div>
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mt-6">
        <div className="flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {features.map((f) => (
              <GlassCard key={f.label} className="flex items-start gap-3 !p-4">
                <IconBox icon={f.icon} size={18} className="bg-orange-500/10 text-orange-400 !w-9 !h-9" />
                <div>
                  <h4 className="text-white font-semibold text-xs">{f.label}</h4>
                  <p className="text-slate-500 text-[11px] mt-0.5">{f.desc}</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
        <div className="w-full lg:w-64 flex flex-col gap-4">
          <GlassCard glow className="text-center">
            <p className="text-4xl font-extrabold text-white" style={{ fontFamily: 'Syne' }}>63M+</p>
            <p className="text-slate-400 text-xs mt-1">MSMEs in India</p>
          </GlassCard>
          <GlassCard className="text-center">
            <p className="text-lg font-bold text-white" style={{ fontFamily: 'Syne' }}>Pricing</p>
            <p className="text-2xl font-bold mt-1"><GradientText>&#8377;799 &ndash; &#8377;4,999</GradientText></p>
            <p className="text-slate-500 text-xs mt-1">per month</p>
          </GlassCard>
          <GlassCard className="!p-3">
            <p className="text-slate-500 text-[11px] text-center">
              Digital India push &bull; UPI explosion &bull; WhatsApp-first culture
            </p>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

/* ========== SLIDE 11: AUSTRALIA MARKET ========== */
const AustraliaMarketSlide = () => {
  const features = [
    { icon: Mail, label: 'SMS + Email', desc: 'Transactional SMS, detailed email comms' },
    { icon: CreditCard, label: 'Stripe / BPAY', desc: 'Local payment methods + Afterpay support' },
    { icon: Shield, label: 'Fair Work Compliance', desc: 'Auto-calc penalty rates, overtime, leave' },
    { icon: Receipt, label: 'ABN / GST / BAS', desc: 'BAS-ready reporting & tax invoice generation' },
    { icon: Heart, label: 'Medicare Integration', desc: 'Health fund claims for medical workflows' },
    { icon: MapPin, label: 'State Holidays', desc: 'Public holidays vary by state + daylight saving' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-australia">
      <div><SectionLabel>Market: Australia</SectionLabel></div>
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-3xl sm:text-4xl font-bold text-white">Australia-First Features</h2>
        <span className="text-3xl">&#127462;&#127482;</span>
      </div>
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mt-6">
        <div className="flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {features.map((f) => (
              <GlassCard key={f.label} className="flex items-start gap-3 !p-4">
                <IconBox icon={f.icon} size={18} className="bg-blue-500/10 text-blue-400 !w-9 !h-9" />
                <div>
                  <h4 className="text-white font-semibold text-xs">{f.label}</h4>
                  <p className="text-slate-500 text-[11px] mt-0.5">{f.desc}</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
        <div className="w-full lg:w-64 flex flex-col gap-4">
          <GlassCard purple className="text-center">
            <p className="text-4xl font-extrabold text-white" style={{ fontFamily: 'Syne' }}>2.5M+</p>
            <p className="text-slate-400 text-xs mt-1">SMEs in Australia</p>
          </GlassCard>
          <GlassCard className="text-center">
            <p className="text-lg font-bold text-white" style={{ fontFamily: 'Syne' }}>Pricing</p>
            <p className="text-2xl font-bold mt-1"><GradientText>$29 &ndash; $149 AUD</GradientText></p>
            <p className="text-slate-500 text-xs mt-1">per month</p>
          </GlassCard>
          <GlassCard className="!p-3">
            <p className="text-slate-500 text-[11px] text-center">
              High digital adoption &bull; Premium ARPU &bull; Cloud-friendly businesses
            </p>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

/* ========== SLIDE 12: COMPETITIVE LANDSCAPE ========== */
const CompetitiveSlide = () => {
  const features = [
    'Multi-Agent AI Teams',
    'Industry-Specific Templates',
    'Country-Aware (India + AU)',
    'WhatsApp + SMS Channels',
    'No-Code Setup',
    'Built-in Booking Engine',
    'Payment Integration',
    'Compliance (GST/Fair Work)',
  ];
  const competitors = [
    { name: 'Novatide', values: [true, true, true, true, true, true, true, true], highlight: true },
    { name: 'Zapier', values: [false, false, false, 'Partial', false, false, false, false] },
    { name: 'Dify.ai', values: [false, 'Limited', false, false, true, false, false, false] },
    { name: 'Mindbody', values: [false, 'Single', false, false, true, true, true, false] },
    { name: 'GoHighLevel', values: [false, false, false, 'SMS only', false, false, 'Partial', false] },
  ];
  const renderVal = (v) => {
    if (v === true) return <Check size={14} className="check-icon" />;
    if (v === false) return <X size={14} className="cross-icon" />;
    return <span className="text-amber-400 text-[10px]">{v}</span>;
  };
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-competition">
      <div><SectionLabel>Competitive Landscape</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white">
          Why <GradientText>Novatide</GradientText> wins
        </h2>
      </div>
      <div>
        <div className="mt-6 rounded-2xl border border-white/5 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="text-left py-3 px-4 text-slate-500 font-medium text-xs">Feature</th>
                {competitors.map((c) => (
                  <th key={c.name} className={`text-center py-3 px-3 text-xs font-semibold ${c.highlight ? 'text-teal-400' : 'text-slate-400'}`}>
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((f, fi) => (
                <tr key={f} className="border-t border-white/[0.03]">
                  <td className="py-2.5 px-4 text-slate-300 text-xs">{f}</td>
                  {competitors.map((c) => (
                    <td key={c.name} className={`text-center py-2.5 px-3 ${c.highlight ? 'bg-teal-500/[0.03]' : ''}`}>
                      {renderVal(c.values[fi])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <p className="text-slate-600 text-xs mt-4 italic">
          Novatide is the only platform combining multi-agent AI, industry templates, and country-aware compliance in one solution.
        </p>
      </div>
    </div>
  );
};

/* ========== SLIDE 13: BUSINESS MODEL ========== */
const BusinessModelSlide = () => {
  const tiers = [
    { name: 'Free', priceIN: 'Free', priceAU: 'Free', features: ['1 AI agent', '50 actions/month', 'Basic dashboard'], highlight: false },
    { name: 'Starter', priceIN: '\u20B9799/mo', priceAU: '$29/mo', features: ['Unlimited workflows', '500 actions/month', 'Booking page + CRM'], highlight: false },
    { name: 'Growth', priceIN: '\u20B91,999/mo', priceAU: '$69/mo', features: ['WhatsApp + SMS', 'Payments integration', 'Advanced analytics'], highlight: true },
    { name: 'Business', priceIN: '\u20B94,999/mo', priceAU: '$149/mo', features: ['Multi-location', 'API access', 'White-label + Priority'], highlight: false },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-business-model">
      <div><SectionLabel>Business Model</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white">
          Revenue <GradientText>streams</GradientText>
        </h2>
      </div>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {tiers.map((t) => (
            <GlassCard key={t.name} glow={t.highlight} className={`flex flex-col ${t.highlight ? '!border-teal-500/40 relative' : ''}`}>
              {t.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><span className="badge-mvp text-[9px]">Popular</span></div>}
              <h3 className="text-white font-bold text-base">{t.name}</h3>
              <p className="text-lg font-bold mt-2"><GradientText>{t.priceIN}</GradientText></p>
              <p className="text-slate-500 text-[10px]">{t.priceAU} AUD</p>
              <ul className="mt-3 space-y-1.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-xs text-slate-400">
                    <Check size={11} className="text-teal-400 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </GlassCard>
          ))}
        </div>
      </div>
      <div>
        <div className="flex flex-col sm:flex-row gap-4 mt-5">
          <GlassCard className="flex-1 !p-4 flex items-center gap-3">
            <Zap size={18} className="text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-white text-xs font-semibold">Usage-Based Add-ons</p>
              <p className="text-slate-500 text-[11px]">SMS/WhatsApp credits &bull; Payment processing fees</p>
            </div>
          </GlassCard>
          <GlassCard className="flex-1 !p-4 flex items-center gap-3">
            <Store size={18} className="text-purple-400 flex-shrink-0" />
            <div>
              <p className="text-white text-xs font-semibold">Agent Marketplace</p>
              <p className="text-slate-500 text-[11px]">Users publish & sell agent templates &bull; 20% commission</p>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

/* ========== SLIDE 14: MARKET OPPORTUNITY ========== */
const MarketOpportunitySlide = () => {
  const stats = [
    { value: '$300B+', label: 'Global SMB SaaS market by 2028', icon: BarChart3, color: 'text-teal-400' },
    { value: '$47B+', label: 'AI Agent market by 2030', icon: Brain, color: 'text-purple-400' },
    { value: '65.5M', label: 'Addressable SMBs (India + Australia)', icon: Users, color: 'text-cyan-400' },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-market">
      <div><SectionLabel>Market Opportunity</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white">
          The wave is <GradientText>massive</GradientText>
        </h2>
      </div>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-6 sm:mt-8">
          {stats.map((s, i) => (
            <GlassCard key={s.label} glow={i === 0} className="text-center flex flex-col items-center">
              <s.icon size={28} className={s.color} />
              <p className="text-4xl sm:text-5xl font-extrabold text-white mt-4" style={{ fontFamily: 'Syne' }}>{s.value}</p>
              <p className="text-slate-400 text-sm mt-2">{s.label}</p>
            </GlassCard>
          ))}
        </div>
      </div>
      <div>
        <GlassCard className="mt-6 !p-5 flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-center">
          <div className="flex-1">
            <h3 className="text-white font-bold text-sm">Conservative Projection</h3>
            <p className="text-slate-400 text-xs mt-1 leading-relaxed">
              0.1% penetration of India + Australia = <span className="text-teal-400 font-semibold">65,500 customers</span>
            </p>
            <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">
              At avg. $30/mo = <span className="text-teal-400 font-semibold">$23.6M ARR</span>
            </p>
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-sm">Market Timing</h3>
            <p className="text-slate-400 text-xs mt-1 leading-relaxed">
              AI agents are the next platform shift. OpenAI, Google, Anthropic all pushing agent paradigms.
              No clear winner in the no-code agent builder space for SMBs.
            </p>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

/* ========== SLIDE 15: ROADMAP ========== */
const RoadmapSlide = () => {
  const phases = [
    {
      phase: 'Phase 1', title: 'MVP Launch', color: 'border-teal-500/50 bg-teal-500/5',
      items: ['Doctor + Tutor + Restaurant', 'India + Australia', 'WhatsApp & SMS channels', 'Basic booking & CRM'],
      tag: 'Now', tagColor: 'bg-teal-500/20 text-teal-300'
    },
    {
      phase: 'Phase 2', title: 'Expand', color: 'border-purple-500/50 bg-purple-500/5',
      items: ['AI Workflow Builder (NLP)', '3 more industries', 'Payment integrations', 'Advanced analytics'],
      tag: 'Q2', tagColor: 'bg-purple-500/20 text-purple-300'
    },
    {
      phase: 'Phase 3', title: 'Localize', color: 'border-cyan-500/50 bg-cyan-500/5',
      items: ['UK, UAE, SEA plugins', 'Multi-language support', 'Country compliance engines', 'Enterprise features'],
      tag: 'Q3-Q4', tagColor: 'bg-cyan-500/20 text-cyan-300'
    },
    {
      phase: 'Phase 4', title: 'Scale', color: 'border-amber-500/50 bg-amber-500/5',
      items: ['Multi-agent team builder', 'Agent marketplace', 'Voice-enabled agents', 'Global expansion'],
      tag: '2027', tagColor: 'bg-amber-500/20 text-amber-300'
    },
  ];
  return (
    <div className="slide-content flex flex-col w-full max-w-6xl mx-auto" data-testid="slide-roadmap">
      <div><SectionLabel>Roadmap</SectionLabel></div>
      <div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white">
          Building the <GradientText>new wave</GradientText>
        </h2>
      </div>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 sm:mt-8">
          {phases.map((p) => (
            <div key={p.phase} className={`rounded-2xl border p-5 ${p.color} flex flex-col`}>
              <div className="flex items-center justify-between">
                <span className="text-slate-500 text-xs font-semibold tracking-wider">{p.phase}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.tagColor}`}>{p.tag}</span>
              </div>
              <h3 className="text-white font-bold text-lg mt-2">{p.title}</h3>
              <ul className="mt-3 space-y-2 flex-1">
                {p.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-slate-400">
                    <ArrowRight size={10} className="text-slate-600 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="text-slate-500 text-sm mt-6">
          Each phase is designed to be self-sustaining &mdash; revenue from Phase 1 funds Phase 2, and so on.
        </p>
      </div>
    </div>
  );
};

/* ========== SLIDE 16: CTA ========== */
const CTASlide = () => (
  <div className="slide-content flex flex-col items-center justify-center min-h-[70vh] text-center" data-testid="slide-cta">
    <div>
      <img src={LOGO} alt="Novatide" className="w-16 h-16 sm:w-20 sm:h-20 mx-auto drop-shadow-2xl" style={{ animation: 'float 4s ease-in-out infinite' }} />
    </div>
    <div>
      <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mt-6">
        <GradientText>Novatide</GradientText>
      </h2>
    </div>
    <div>
      <p className="text-lg sm:text-xl md:text-2xl text-slate-300 mt-4 font-light max-w-2xl px-2">
        The future of local business runs on intelligent agents
      </p>
    </div>
    <div>
      <p className="text-base sm:text-lg text-slate-500 mt-2 px-2">
        Let&rsquo;s build the new wave together.
      </p>
    </div>
    <div>
      <a href="https://novatide.app" target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-3 rounded-full bg-gradient-to-r from-teal-500 to-purple-600 text-white font-semibold text-sm tracking-wide shadow-lg shadow-teal-500/20 hover:shadow-teal-500/30 hover:scale-105 transition-all cursor-pointer no-underline mt-8 sm:mt-10" data-testid="get-started-btn">
        Get Started
      </a>
    </div>
    <div>
      <p className="text-slate-700 text-xs mt-10 sm:mt-12">
        novatide.ai &bull; hello@novatide.ai
      </p>
    </div>
  </div>
);

/* ========== MAIN PITCH DECK COMPONENT ========== */

const allSlides = [
  TitleSlide,
  ProblemSlide,
  SolutionSlide,
  HowItWorksSlide,
  AICrewSlide,
  IndustriesSlide,
  DoctorSlide,
  TutorSlide,
  RestaurantSlide,
  IndiaMarketSlide,
  AustraliaMarketSlide,
  CompetitiveSlide,
  BusinessModelSlide,
  MarketOpportunitySlide,
  RoadmapSlide,
  CTASlide,
];

const PitchDeck = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  const total = allSlides.length;

  useEffect(() => {
    document.title = 'Novatide \u2014 AI-Powered Operating System for Local Businesses';
  }, []);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent(c => Math.min(c + 1, total - 1));
  }, [total]);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(c => Math.max(c - 1, 0));
  }, []);

  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (diff > 60) next();
    else if (diff < -60) prev();
    setTouchStart(null);
  };

  const toggleFullscreen = useCallback(() => {
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('Fullscreen not supported');
      }
    }
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
        e.preventDefault();
        prev();
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, toggleFullscreen]);

  const CurrentSlide = allSlides[current];
  const animClass = direction === 1 ? 'slide-animate-right' : 'slide-animate-left';

  return (
    <div
      className="pitch-deck"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="pitch-deck"
    >
      {/* Background effects */}
      <div className="bg-blob bg-blob-1" />
      <div className="bg-blob bg-blob-2" />
      <div className="bg-blob bg-blob-3" />

      {/* Progress bar */}
      <div className="progress-bar" data-testid="progress-bar">
        <div
          className="progress-bar-fill"
          style={{ width: `${((current + 1) / total) * 100}%` }}
        />
      </div>

      {/* Slide content */}
      <div key={current} className={`slide-wrapper ${animClass}`}>
        <CurrentSlide />
      </div>

      {/* Navigation */}
      <div className="nav-controls">
        <button
          onClick={prev}
          disabled={current === 0}
          className="nav-btn"
          data-testid="nav-prev"
          aria-label="Previous slide"
        >
          <ChevronLeft size={18} />
        </button>
        <span className="slide-counter" data-testid="slide-counter">
          {current + 1} / {total}
        </span>
        <button
          onClick={next}
          disabled={current === total - 1}
          className="nav-btn"
          data-testid="nav-next"
          aria-label="Next slide"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Fullscreen toggle */}
      <button
        onClick={toggleFullscreen}
        className="fullscreen-btn"
        data-testid="fullscreen-toggle"
        aria-label="Toggle fullscreen"
      >
        {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
      </button>
    </div>
  );
};

export default PitchDeck;
